import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../../components/ui/sheet";
import { HomeOSAvatar } from "./components/HomeOSAvatar";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import {
  identityLine, countKnownThings, migrateMemberToV2,
  FOOD_FIELDS, CARE_FIELDS, LIVING_TEXT_FIELDS, CELEBRATION_LIST_FIELDS,
} from "./utils/memberIntelligence";

/* Local mood-zone colours to avoid cross-file babel-metadata traversal issues. */
const MOOD_ZONE_COLORS = {
  yellow: { tile: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-900 dark:text-amber-100", dot: "bg-amber-500" },
  red:    { tile: "bg-rose-100 dark:bg-rose-900/30",   text: "text-rose-900 dark:text-rose-100",   dot: "bg-rose-500" },
  green:  { tile: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-900 dark:text-emerald-100", dot: "bg-emerald-500" },
  blue:   { tile: "bg-sky-100 dark:bg-sky-900/30",     text: "text-sky-900 dark:text-sky-100",     dot: "bg-sky-500" },
};

const inter = { fontFamily: "Inter, system-ui, sans-serif" };
const serif = { fontFamily: "var(--ara-font-display), 'Times New Roman', serif" };

/* ═══════════════════════════════════════════════
   ENERGY / MOOD BADGES (shared, dashboard compact card)
   ═══════════════════════════════════════════════ */

const energyCfg = {
  Low:    { cls: 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300', dot: 'bg-rose-500' },
  Medium: { cls: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300', dot: 'bg-amber-500' },
  Good:   { cls: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300', dot: 'bg-emerald-500' },
};

function EnergyBadge({ level }) {
  const c = energyCfg[level] || energyCfg.Medium;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold ${c.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />{level}
    </span>
  );
}

function MoodBadge({ checkin }) {
  if (!checkin) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-gray-100 dark:bg-slate-700/60 text-gray-500 dark:text-gray-400" data-testid="mood-badge-waiting">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />Waiting
      </span>
    );
  }
  const z = MOOD_ZONE_COLORS[checkin.zone] || MOOD_ZONE_COLORS.green;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold ${z.tile} ${z.text}`} data-testid="mood-badge">
      <span className={`w-1.5 h-1.5 rounded-full ${z.dot}`} />{checkin.moodLabel}
    </span>
  );
}

/* ═══════════════════════════════════════════════
   COMPACT CARD (for Home OS dashboard)
   ═══════════════════════════════════════════════ */

export function FamilyMemberCardCompact({ member }) {
  const moodCheckin = member?._moodCheckin || null;
  return (
    <div
      className="snap-start shrink-0 w-[190px] sm:w-[210px] bg-white dark:bg-slate-800/80 rounded-[24px] border border-gray-100 dark:border-slate-700/60 p-4 flex flex-col transition-shadow hover:shadow-md"
      data-testid={`family-member-${member.id}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <HomeOSAvatar member={member} size="md" />
        <div className="min-w-0 flex-1">
          <p className="text-base font-extrabold text-gray-900 dark:text-gray-50 truncate">{member.name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{identityLine(member) || member.role}</p>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500 dark:text-gray-400 font-bold">Mood</span>
          <MoodBadge checkin={moodCheckin} />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500 dark:text-gray-400 font-bold">Energy</span>
          <EnergyBadge level={member.energy} />
        </div>
      </div>
      {(member.araShouldKnow?.[0] || member.note) && (
        <div className="mt-3 pt-2 border-t border-gray-100 dark:border-slate-700/50">
          <span className="inline-block text-[10px] font-bold text-gray-500 bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded-full truncate max-w-full">{member.araShouldKnow?.[0] || member.note}</span>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   FULL ENTRY (Family Room — calm editorial row)
   ═══════════════════════════════════════════════ */

export function FamilyMemberCardFull({ member: rawMember, onEdit }) {
  const [open, setOpen] = useState(false);
  const { goals, tasks } = useHomeOSState();

  const member = useMemo(() => migrateMemberToV2(rawMember), [rawMember]);

  const memberGoals = useMemo(
    () => (goals || []).filter(g => g.ownerType === "member" && g.ownerMemberId === member.id),
    [goals, member.id]
  );
  /* Read-only context sourced from the central task system. */
  const memberTasks = useMemo(
    () => (tasks || []).filter(t => t.assignedTo === member.id && t.status !== "Done" && t.status !== "Completed"),
    [tasks, member.id]
  );

  const knownCount = countKnownThings(member, memberGoals);
  const identity = identityLine(member);
  const firstName = (member.preferredName || member.name || "").split(" ")[0];

  return (
    <>
      <div className="py-6" data-testid={`family-card-${member.id}`}>
        <div className="flex items-start gap-4">
          <HomeOSAvatar member={member} size="md" />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <h3 className="text-[16px] leading-tight" style={{ ...inter, color: "var(--ara-ink)", fontWeight: 500 }}>
                {member.name}
              </h3>
              {knownCount > 0 && (
                <span className="text-[11.5px]" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }}>
                  {knownCount} thing{knownCount === 1 ? "" : "s"} recorded
                </span>
              )}
            </div>
            {identity && (
              <p className="mt-0.5 text-[12.5px]" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }}>
                {identity}
              </p>
            )}
            {memberTasks.length > 0 && (
              <p className="mt-2 text-[13px] leading-[1.5]" style={{ ...inter, color: "var(--ara-ink-muted)", fontWeight: 400 }}>
                {memberTasks.length} thing{memberTasks.length === 1 ? "" : "s"} assigned today
              </p>
            )}
            {member.araShouldKnow?.[0] && (
              <p className="mt-1 italic text-[13px] leading-[1.5]" style={{ ...serif, color: "var(--ara-ink-muted)", fontWeight: 400 }}>
                {member.araShouldKnow[0]}
              </p>
            )}
            <div className="mt-3 flex items-baseline gap-x-6">
              <button
                onClick={() => setOpen(true)}
                className="text-[13px] transition-opacity hover:opacity-70"
                style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
                data-testid={`open-profile-${member.id}`}
              >
                Open profile →
              </button>
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="text-[12.5px] transition-opacity hover:opacity-70"
                  style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400, background: "none", border: "none", cursor: "pointer" }}
                  data-testid={`edit-profile-${member.id}`}
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Sheet — a beautifully kept record of someone you love */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[480px] overflow-y-auto p-0"
          style={{ background: "var(--ara-surface)" }}
          aria-describedby={undefined}
        >
          <SheetHeader className="p-6 pb-5 sticky top-0 z-10" style={{ background: "var(--ara-surface)", borderBottom: "1px solid var(--ara-line-soft)" }}>
            <div className="flex items-center gap-4">
              <HomeOSAvatar member={member} size="lg" />
              <div className="min-w-0">
                <SheetTitle className="text-[20px] leading-tight" style={{ ...serif, color: "var(--ara-ink)", fontWeight: 400 }}>
                  {member.name}
                </SheetTitle>
                <p className="mt-0.5 text-[13px]" style={{ ...inter, color: "var(--ara-ink-soft)" }}>
                  {identity || "Household member"}
                </p>
                {onEdit && (
                  <button
                    type="button"
                    onClick={() => { setOpen(false); onEdit(); }}
                    className="mt-2 text-[12.5px] transition-opacity hover:opacity-70"
                    style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}
                    data-testid={`profile-edit-link-${member.id}`}
                  >
                    Edit profile →
                  </button>
                )}
              </div>
            </div>
          </SheetHeader>

          <ProfileBody
            member={member}
            firstName={firstName}
            knownCount={knownCount}
            memberGoals={memberGoals}
            memberTasks={memberTasks}
            onEdit={onEdit ? () => { setOpen(false); onEdit(); } : null}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}

/* ── Read-only profile rendering — only populated sections appear. ── */

function SectionLabel({ children }) {
  return (
    <p className="text-[11px] tracking-[0.16em] uppercase pb-2" style={{ ...inter, color: "var(--ara-mod-family, #7A6E63)", fontWeight: 500, borderBottom: "1px solid var(--ara-line-soft)" }}>
      {children}
    </p>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-6 py-1.5">
      <span className="text-[13px] flex-shrink-0" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }}>{k}</span>
      <span className="text-[13.5px] text-right" style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450 }}>{v}</span>
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="mt-1 space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-[13.5px] leading-[1.55]" style={{ ...inter, color: "var(--ara-ink)" }}>
          <span className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: "var(--ara-mod-family, #7A6E63)" }} />
          {item}
        </li>
      ))}
    </ul>
  );
}

/* Rows of [label, list] — renders only non-empty lists. */
function ListGroup({ pairs }) {
  return (
    <div className="mt-3 space-y-4">
      {pairs.map(([label, items]) => (
        <div key={label}>
          <p className="text-[12px]" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 450 }}>{label}</p>
          <BulletList items={items} />
        </div>
      ))}
    </div>
  );
}

function ProfileBody({ member, firstName, knownCount, memberGoals, memberTasks, onEdit }) {
  const nonEmpty = (arr) => Array.isArray(arr) && arr.length > 0;
  const filled = (v) => String(v || "").trim();

  const aboutRows = [
    ["Preferred name", filled(member.preferredName)],
    ["Birthday", filled(member.birthday)],
    ["Pronouns", filled(member.pronouns)],
    ["Contact", filled(member.contactInfo)],
  ].filter(([, v]) => v);

  const f = member.food || {};
  const foodPairs = FOOD_FIELDS.map(([k, label]) => [label, f[k]]).filter(([, v]) => nonEmpty(v));

  const favEntries = member.likesFavourites || [];
  const favByCategory = favEntries.reduce((acc, e) => {
    (acc[e.category] = acc[e.category] || []).push(e.value);
    return acc;
  }, {});

  const c = member.care || {};
  const carePairs = CARE_FIELDS.map(([k, label]) => [label, c[k]]).filter(([, v]) => nonEmpty(v));

  const l = member.living || {};
  const livingRows = [
    ["Rhythm", filled(l.chronotype)],
    ...LIVING_TEXT_FIELDS.map(([k, label]) => [label, filled(l[k])]),
  ].filter(([, v]) => v);
  const commitments = l.commitments || [];

  const cel = member.celebrations || {};
  const celPairs = CELEBRATION_LIST_FIELDS.map(([k, label]) => [label, cel[k]]).filter(([, v]) => nonEmpty(v));
  const celRows = [
    ["Birthday", filled(member.birthday)],
    ["Clothing size", filled(cel.clothingSize)],
    ["Shoe size", filled(cel.shoeSize)],
  ].filter(([, v]) => v);

  const hasCelebrations = celPairs.length > 0 || filled(cel.clothingSize) || filled(cel.shoeSize);
  const hasDetail =
    aboutRows.length > 0 || foodPairs.length > 0 || favEntries.length > 0 ||
    carePairs.length > 0 || livingRows.length > 0 || commitments.length > 0 ||
    nonEmpty(member.responsibilities) || memberGoals.length > 0 ||
    hasCelebrations || nonEmpty(member.araShouldKnow);

  return (
    <div className="p-6 space-y-8" data-testid={`profile-sheet-${member.id}`}>
      {knownCount >= 3 && (
        <p className="italic text-[14px] leading-[1.55] -mt-1" style={{ ...serif, color: "var(--ara-ink-muted)" }} data-testid="ara-knows-count">
          ARA knows {knownCount} things about {firstName}.
        </p>
      )}

      {aboutRows.length > 0 && (
        <section data-testid="profile-section-about">
          <SectionLabel>About</SectionLabel>
          <div className="mt-2">{aboutRows.map(([k, v]) => <KV key={k} k={k} v={v} />)}</div>
        </section>
      )}

      {/* Read-only, sourced from the central task system */}
      {memberTasks.length > 0 && (
        <section data-testid="profile-section-today">
          <SectionLabel>Today</SectionLabel>
          <p className="mt-2 text-[13px]" style={{ ...inter, color: "var(--ara-ink-soft)" }}>
            {memberTasks.length} thing{memberTasks.length === 1 ? "" : "s"} assigned
          </p>
          <BulletList items={memberTasks.slice(0, 4).map(t => t.title)} />
          <Link
            to="/home-os/chores"
            className="inline-block mt-3 text-[13px] transition-opacity hover:opacity-70"
            style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450, textDecoration: "underline", textUnderlineOffset: 3 }}
            data-testid="profile-see-all-tasks"
          >
            See all tasks →
          </Link>
        </section>
      )}

      {foodPairs.length > 0 && (
        <section data-testid="profile-section-food">
          <SectionLabel>Food & meals</SectionLabel>
          <ListGroup pairs={foodPairs} />
        </section>
      )}

      {favEntries.length > 0 && (
        <section data-testid="profile-section-favourites">
          <SectionLabel>Likes & favourites</SectionLabel>
          <div className="mt-3 space-y-2">
            {Object.entries(favByCategory).map(([cat, values]) => (
              <KV key={cat} k={cat} v={values.join(", ")} />
            ))}
          </div>
        </section>
      )}

      {carePairs.length > 0 && (
        <section data-testid="profile-section-care">
          <SectionLabel>Health & care</SectionLabel>
          <ListGroup pairs={carePairs} />
          <p className="mt-3 text-[12px] italic" style={{ ...serif, color: "var(--ara-ink-muted)" }}>
            Detailed health information lives in Wellness.
          </p>
        </section>
      )}

      {(livingRows.length > 0 || commitments.length > 0) && (
        <section data-testid="profile-section-living">
          <SectionLabel>Routines & preferences</SectionLabel>
          <div className="mt-2">{livingRows.map(([k, v]) => <KV key={k} k={k} v={v} />)}</div>
          {commitments.length > 0 && (
            <div className="mt-3">
              <p className="text-[12px]" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 450 }}>Weekly commitments</p>
              <BulletList items={commitments} />
            </div>
          )}
        </section>
      )}

      {nonEmpty(member.responsibilities) && (
        <section data-testid="profile-section-responsibilities">
          <SectionLabel>Responsibilities</SectionLabel>
          <BulletList items={member.responsibilities} />
        </section>
      )}

      {memberGoals.length > 0 && (
        <section data-testid="profile-section-goals">
          <SectionLabel>Goals & dreams</SectionLabel>
          <div className="mt-3 space-y-3">
            {memberGoals.map((g) => (
              <div key={g.id}>
                <p className="text-[14px]" style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450 }}>{g.title}</p>
                <p className="mt-0.5 text-[12px]" style={{ ...inter, color: "var(--ara-ink-soft)" }}>
                  {[g.category, g.target, g.targetDate].filter(Boolean).join(" · ")}
                </p>
                {g.why && <p className="mt-0.5 text-[12.5px] italic" style={{ ...serif, color: "var(--ara-ink-muted)" }}>{g.why}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {hasCelebrations && (
        <section data-testid="profile-section-celebrations">
          <SectionLabel>Celebrations</SectionLabel>
          {celRows.length > 0 && <div className="mt-2">{celRows.map(([k, v]) => <KV key={k} k={k} v={v} />)}</div>}
          <ListGroup pairs={celPairs} />
        </section>
      )}

      {nonEmpty(member.araShouldKnow) && (
        <section data-testid="profile-section-ara">
          <SectionLabel>ARA should know</SectionLabel>
          <div className="mt-3 space-y-2">
            {member.araShouldKnow.map((note, i) => (
              <p key={i} className="italic text-[14px] leading-[1.6]" style={{ ...serif, color: "var(--ara-ink-muted)" }}>
                {note}
              </p>
            ))}
          </div>
        </section>
      )}

      {!hasDetail && (
        <div data-testid="profile-sparse-invite">
          <p className="italic text-[15px] leading-[1.55]" style={{ ...serif, color: "var(--ara-ink-muted)" }}>
            This profile can grow with your household.
          </p>
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="mt-4 text-[13px] transition-opacity hover:opacity-70"
              style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}
              data-testid="profile-add-details-btn"
            >
              Add more details →
            </button>
          )}
        </div>
      )}

      {hasDetail && onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-[13px] transition-opacity hover:opacity-70"
          style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}
          data-testid="profile-add-details-btn"
        >
          Add more details →
        </button>
      )}
    </div>
  );
}
